/**
 * The visit's edit sheet — `docs/visit-page.md` §7.3: About *Edit* (while the
 * visit is open), *Edit plan* (unscheduled, Board 13 B, §17.3) and the phone
 * header's pencil (Board 16 A) open it. The name, the crew, the yard, the
 * planned hours — the plan lines are never edited (§2.5).
 *
 * **Drawn on no board** (lead, Oct 29 — a DEVIATION REQUEST for Aaron): a
 * centred `VisitPopup` (Board 16 D's sheet on a phone) with About's own
 * labels — *Visit · Crew · Leaves from · Planned*. On a series visit Save asks
 * how far the change goes first (Board 14, D20). `PATCH /visits/:id` carries
 * the read's `version`; the server answers the page re-read.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ApplyTo, EditVisitInput, VisitEditOptions, VisitRead } from '@rowted/shared'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { EDIT_VISIT } from './copy.js'
import { visitPath, useVisitWrite, type VisitWriteResult } from './api.js'
import { hoursBare } from './format.js'
import { HowFarPopover } from './how-far.js'
import { FIELD_BOX, FIELD_INPUT, FieldLabel } from './fields.js'
import { PickField, type PickOption } from './equipment-fields.js'
import { VISIT_LAYER, VisitButton, VisitPopup } from './sheet.js'

export function EditVisitSheet({ read, onClose, onWritten }: {
  read: VisitRead
  onClose: () => void
  onWritten: (result: VisitWriteResult) => void
}) {
  const phone = usePhone()
  const write = useVisitWrite(read.id)
  const about = read.rail.about
  const [options, setOptions] = useState<VisitEditOptions | null>(null)
  const [name, setName] = useState(read.header.name)
  const [crewId, setCrewId] = useState<string | null>(about.crew?.crew.id ?? null)
  const [yardId, setYardId] = useState<string | null>(about.yard?.id ?? null)
  const [planned, setPlanned] = useState(hoursBare(about.planned.hours))
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const saveAt = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let live = true
    request<VisitEditOptions>(visitPath(read.id, '/edit-options'))
      .then((answer) => { if (live) setOptions(answer) })
      .catch((cause: unknown) => { if (live) setProblem(cause instanceof Error ? cause.message : String(cause)) })
    return () => { live = false }
  }, [read.id])

  const crews: PickOption[] = useMemo(() => (options?.crews ?? []).map((row) => ({
    value: row.crew.id,
    label: row.lead ? EDIT_VISIT.crewLead(row.crew.name, row.lead.name) : row.crew.name,
  })), [options])
  const yards: PickOption[] = useMemo(() => (options?.yards ?? []).map((yard) => ({ value: yard.id, label: yard.name })), [options])

  /* What changed — only those go (the schema refuses an empty edit). */
  const hours = Number(planned)
  const plannedValid = planned.trim() !== '' && Number.isFinite(hours) && hours > 0
  const change: Omit<EditVisitInput, 'version' | 'applyTo'> = {
    ...(name.trim() !== read.header.name ? { name: name.trim() } : {}),
    ...(crewId !== (about.crew?.crew.id ?? null) ? { crewId } : {}),
    ...(yardId !== (about.yard?.id ?? null) ? { yardId } : {}),
    ...(plannedValid && planned.trim() !== hoursBare(about.planned.hours) ? { plannedHours: hours } : {}),
  }
  const changed = Object.keys(change).length > 0
  const ready = changed && name.trim() !== '' && plannedValid && !busy
  const asksHowFar = Boolean(read.series && read.series.after.count > 0)

  const save = async (applyTo: ApplyTo) => {
    setBusy(true)
    setProblem(null)
    try {
      const body: EditVisitInput = { version: read.version, applyTo, ...change }
      onWritten(await write('PATCH', '', body, { tab: 'overview' }))
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
      setAsking(false)
      setBusy(false)
    }
  }

  const chooseCrew = (next: string | null) => {
    setCrewId(next)
    /* A crew leaves from its own yard (§7.3 *Leaves from*) — follow it unless the yard was chosen apart. */
    const home = options?.crews.find((row) => row.crew.id === next)?.yardId ?? null
    if (home && yardId === (options?.crews.find((row) => row.crew.id === crewId)?.yardId ?? about.yard?.id ?? null)) setYardId(home)
  }

  const size = phone ? 'phone' : 'popup'
  return (
    <>
      <VisitPopup
        width={470}
        layer={VISIT_LAYER}
        title={EDIT_VISIT.title}
        sub={read.reference}
        onClose={onClose}
        asDrawn="visit edit"
        footer={(
          <div ref={saveAt} className="contents">
            <VisitButton size={size} onClick={onClose}>{EDIT_VISIT.cancel}</VisitButton>
            <VisitButton size={size} tone="primary" disabled={!ready}
              onClick={() => (asksHowFar ? setAsking(true) : void save('this'))}>
              {EDIT_VISIT.save}
            </VisitButton>
          </div>
        )}
      >
        <div className="flex flex-col gap-3.5" data-visit-edit="">
          <div>
            <FieldLabel htmlFor="visit-edit-name" required>{EDIT_VISIT.visit}</FieldLabel>
            <div className={FIELD_BOX}>
              <input id="visit-edit-name" value={name} maxLength={200} onChange={(event) => setName(event.target.value)}
                className={FIELD_INPUT} />
            </div>
          </div>
          <div>
            <FieldLabel htmlFor="visit-edit-crew">{EDIT_VISIT.crew}</FieldLabel>
            <PickField id="visit-edit-crew" label={EDIT_VISIT.crew} value={crewId} options={crews} onChange={chooseCrew} phone={phone} clearable />
          </div>
          <div>
            <FieldLabel htmlFor="visit-edit-yard">{EDIT_VISIT.leavesFrom}</FieldLabel>
            <PickField id="visit-edit-yard" label={EDIT_VISIT.leavesFrom} value={yardId} options={yards} onChange={setYardId} phone={phone} clearable />
          </div>
          <div>
            <FieldLabel htmlFor="visit-edit-planned" required>{EDIT_VISIT.planned}</FieldLabel>
            <div className={`${FIELD_BOX} w-[140px]`}>
              <input id="visit-edit-planned" inputMode="decimal" value={planned}
                onChange={(event) => setPlanned(event.target.value.replace(/[^\d.]/g, ''))}
                className={`${FIELD_INPUT} tabular-nums`} />
              <span className="pl-1.5 text-text-muted">{EDIT_VISIT.unit}</span>
            </div>
          </div>
          {problem && <p className="m-0 text-[12px] text-danger">{problem}</p>}
        </div>
      </VisitPopup>
      {asking && read.series && (
        <HowFarPopover
          anchor={saveAt.current?.lastElementChild instanceof HTMLElement ? saveAt.current.lastElementChild : null}
          series={read.series}
          date={read.header.meta.dates.first ?? read.series.first}
          heading={EDIT_VISIT.howFar}
          busy={busy}
          over
          onSave={(applyTo) => void save(applyTo)}
          onCancel={() => setAsking(false)}
        />
      )}
    </>
  )
}
